import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { toneDot } from '../campaign-status.js';
import { applyAnalysisToOffering } from '../offering-apply.js';
import { toast } from '../toast.js';
import type { ParsedDocument } from './document-parser.js';
import { DocumentChat } from './document-chat.js';
import { DocumentUpload, type DocumentUploadState } from './document-upload.js';
import { hasManualContent as manualFilledIn, openSections, withoutTalkingPoint, withTalkingPoint } from './logic/offering.js';
import type { AnalysisResult, OfferingConfig } from './types.js';

export interface WizOfferingProps {
  offering: OfferingConfig;
  onOfferingChange: (offering: OfferingConfig) => void;
  /** Bubbles up while a document/website is being parsed or the AI is analyzing. */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Upload working-set (files / websites / cloud files) lifted to the parent so
   * it survives step navigation — the wizard re-mounts this step via `keyed`,
   * which would otherwise discard the in-memory upload state.
   */
  uploadState?: DocumentUploadState;
  onUploadStateChange?: (state: DocumentUploadState) => void;
  /** Parsed documents, also lifted so the AI analysis card reappears on return. */
  parsedDocs?: ParsedDocument[];
  onParsedDocsChange?: (docs: ParsedDocument[]) => void;
}

export const WizOffering = stateful((props: WizOfferingProps) => {
  const { offering, onOfferingChange } = props;
  const [newPoint, setNewPoint] = useState('');
  const [parsedDocs, setParsedDocs] = useState<ParsedDocument[]>(props.parsedDocs ?? []);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);

  // Combine the two long-running sources. Chat busy only counts while documents
  // are present so an unmounting chat (docs removed) can't leave us stuck busy.
  const busy = uploadBusy || (chatBusy && parsedDocs.length > 0);
  useEffect(() => {
    props.onBusyChange?.(busy);
  }, [busy]);

  // Whether a manual offering is already filled in — if so, open the manual
  // section by default, otherwise start with AI-Powered open and manual collapsed;
  // AI-Powered stays open when uploads/websites were restored (logic/offering.ts).
  const hasManualContent = manualFilledIn(offering);
  const sections = openSections(offering, {
    parsedDocs: parsedDocs.length,
    websites: props.uploadState?.websites.length ?? 0,
    files: props.uploadState?.files.length ?? 0,
    cloudFiles: props.uploadState?.cloudFiles.length ?? 0,
  });
  const [aiOpen, setAiOpen] = useState(sections.aiOpen);
  const [manualOpen, setManualOpen] = useState(sections.manualOpen);

  const update = (patch: Partial<OfferingConfig>) => {
    onOfferingChange({ ...offering, ...patch });
  };

  const addPoint = () => {
    const next = withTalkingPoint(offering, newPoint);
    if (!next) return;
    update({ talkingPoints: next.talkingPoints });
    setNewPoint('');
  };

  const removePoint = (index: number) => {
    update({ talkingPoints: withoutTalkingPoint(offering, index).talkingPoints });
  };

  const handleAnalysisApproved = (result: AnalysisResult) => {
    const sourceDocuments = [
      ...(offering.sourceDocuments ?? []),
      ...parsedDocs.map(d => {
        const source: 'local' | 'gdrive' | 'onedrive' | 'website' =
          d.fileType === 'website'
            ? 'website'
            : d.fileType === 'gdoc' || d.fileType === 'gsheet' || d.fileType === 'gslide'
              ? 'gdrive'
              : d.sourceUrl?.includes('drive.google.com')
                ? 'gdrive'
                : 'local';
        return {
          fileName: d.fileName,
          uploadedAt: new Date().toISOString(),
          ...(d.sourceUrl ? { url: d.sourceUrl } : {}),
          source,
        };
      }),
    ];
    // Apply OVERWRITES the manual name and description, every time — see
    // applyAnalysisToOffering. Talking points accumulate (deduped).
    update({
      ...applyAnalysisToOffering(offering, result),
      sourceDocuments,
    });
    // After applying AI results: collapse AI section, open manual one so the
    // user immediately sees what was filled in.
    setAiOpen(false);
    setManualOpen(true);
    // Make the hand-off explicit — testers were unsure when AI results are
    // expected to populate the manual fields.
    toast.success('AI analysis applied — review and edit the filled fields under “Manual offering” below.');
  };

  return html`
    <div class="flex flex-col gap-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
          <span class="icon-[material-symbols--storefront] text-xl"></span>
        </span>
        <div>
          <h2 class="text-[0.9375rem] font-medium">Define your offering</h2>
          <p class="text-base-content/70 text-sm">
            Describe the product or service your SDR will pitch. The AI uses this to generate personalized messages.
          </p>
        </div>
      </div>

      <!-- ==================== AI-POWERED OFFERING (collapsible) ==================== -->
      <div class="card bg-base-100">
        <button
          class="card-body flex flex-row items-center gap-3 px-6 py-4 text-left cursor-pointer hover:bg-(--sdr-hover) transition rounded-box"
          @click=${() => setAiOpen(!aiOpen)}
          aria-expanded=${aiOpen ? 'true' : 'false'}
        >
          <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg shrink-0">
            <span class="icon-[material-symbols--auto-awesome] text-xl"></span>
          </span>
          <div class="flex-1">
            <h3 class="text-[0.9375rem] font-medium">AI-powered offering</h3>
            <p class="text-base-content/60 text-xs mt-0.5">
              Upload product collateral and let the AI generate name, description, and talking points — when you apply
              the result, it fills the editable “Manual offering” fields below.
            </p>
          </div>
          <span
            class="icon-[material-symbols--expand-more] text-2xl text-base-content/50 transition-transform"
            style=${aiOpen ? 'transform: rotate(180deg)' : ''}
          ></span>
        </button>
        ${
          aiOpen
            ? html`
          <div class="ndi-expand px-6 pb-6 -mt-2 flex flex-col gap-3">
            ${DocumentUpload({
              onDocumentsParsed: docs => {
                setParsedDocs(docs);
                props.onParsedDocsChange?.(docs);
              },
              onBusyChange: setUploadBusy,
              initialState: props.uploadState,
              onStateChange: props.onUploadStateChange,
            })}
            ${
              parsedDocs.length > 0
                ? DocumentChat({
                    documents: parsedDocs,
                    onAnalysisApproved: handleAnalysisApproved,
                    onBusyChange: setChatBusy,
                  })
                : nothing
            }
          </div>
        `
            : nothing
        }
      </div>

      <!-- ==================== MANUAL OFFERING (collapsible) ==================== -->
      <div class="card bg-base-100">
        <button
          class="card-body flex flex-row items-center gap-3 px-6 py-4 text-left cursor-pointer hover:bg-(--sdr-hover) transition rounded-box"
          @click=${() => setManualOpen(!manualOpen)}
          aria-expanded=${manualOpen ? 'true' : 'false'}
        >
          <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg shrink-0">
            <span class="icon-[material-symbols--edit-note] text-xl"></span>
          </span>
          <div class="flex-1">
            <h3 class="text-[0.9375rem] font-medium">Manual offering</h3>
            <p class="text-base-content/60 text-xs mt-0.5">
              ${
                hasManualContent
                  ? 'Review or edit your product info manually.'
                  : 'Define your product info manually — AI results will fill these fields when applied.'
              }
            </p>
          </div>
          ${
            hasManualContent
              ? toneDot({ tone: 'positive', label: 'Filled' })
              : nothing
          }
          <span
            class="icon-[material-symbols--expand-more] text-2xl text-base-content/50 transition-transform"
            style=${manualOpen ? 'transform: rotate(180deg)' : ''}
          ></span>
        </button>
        ${
          manualOpen
            ? html`
          <div class="ndi-expand px-6 pb-6 -mt-2 flex flex-col gap-4">
            <!-- Product / Service Name -->
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">Product or service name</span>
              ${TextInput({
                value: offering.name,
                onPicked: value => update({ name: value ?? '' }),
                placeholder: 'e.g., AI-Powered Sales Automation Platform',
              })}
            </div>

            <!-- Description -->
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">Description</span>
              ${TextareaInput({
                value: offering.description,
                onPicked: value => update({ description: value ?? '' }),
                placeholder: 'What does your product/service do? What problems does it solve?',
                rows: 4,
              })}
            </div>

            <!-- Key Talking Points -->
            <div class="flex flex-col gap-2">
              <span class="text-sm font-medium">Key talking points</span>
              <p class="text-base-content/60 text-xs">
                Short selling points, proof points, or numbers the AI weaves into your outreach messages — e.g. results
                you deliver, customer references, or differentiators. Optional, but each point gives the AI concrete
                material to personalize with.
              </p>

              ${
                offering.talkingPoints.length > 0
                  ? html`
                    <div class="flex flex-col gap-1">
                      ${offering.talkingPoints.map(
                        (point, i) => html`
                          <div class="border-base-300 flex items-center gap-2 rounded-lg border px-3 py-2">
                            <span class="flex-1 text-sm">${point}</span>
                            <button class="btn btn-ghost btn-xs btn-square" @click=${() => removePoint(i)}>
                              <span class="icon-[material-symbols--close] text-base"></span>
                            </button>
                          </div>
                        `,
                      )}
                    </div>
                  `
                  : html`<p class="text-base-content/40 text-sm italic">
                    No talking points yet — add one below, or let the AI extract them from your documents in
                    “AI-powered offering” above.
                  </p>`
              }

              <div class="flex items-center gap-2">
                <div class="flex-1">
                  ${TextInput({
                    value: newPoint,
                    onChanged: value => setNewPoint(value ?? ''),
                    onSubmit: () => addPoint(),
                    placeholder: 'e.g., Clients see 40-60% open rates',
                  })}
                </div>
                <button class="btn flex-none" @click=${() => addPoint()}>
                  <span class="icon-[material-symbols--add] text-lg"></span>
                  Add
                </button>
              </div>

              ${
                offering.talkingPoints.length > 0
                  ? html`<span class="text-base-content/50 text-xs"
                    >${offering.talkingPoints.length} point${offering.talkingPoints.length === 1 ? '' : 's'}</span
                  >`
                  : nothing
              }
            </div>
          </div>
        `
            : nothing
        }
      </div>
    </div>
  `;
});
